import { Howl, Howler } from 'howler'; // https://github.com/goldfire/howler.js#documentation
import Player from './Player';
import _ from 'lodash';

// NOTE: HowerPlayer is a subclass of Player, so inherits all its methods
// Anything common to all Players is in Player.
// Anything unique to this implementation is here in HowlerPlayer

export default class HowlerPlayer extends Player {
	constructor(config = {}) {
		super(...arguments);

		const defaults = {

			/**
			 * Rate of playback. 
			 * Float from 0.5 to 4.0, with 1.0 being normal speed.
			 */
			rate: 1.0,

			/**
			 * Settings to pass to each new "Howl"
			 */
			settings: {
				volume: 1.0,
				html5: true, // Set to true to force HTML5 Audio. This should be used for large audio files so that you don't have to wait for the full file to be downloaded and decoded before playing.
				loop: false,
				preload: true,
				autoplay: false,
				mute: false,
				rate: 1,
				pool: 8,
				format: null,
				xhrWithCredentials: false,
				// onload: () => { console.log('Howler.onload'); },
				// onloaderror: (id, error) => { console.log('Howler.onloaderror', id, error); },
				onplayerror: (id, error) => {
					console.log('Howler.onplayerror', id, error);
				},
				playerror: (id, error) => {
					console.log('Howler.playerror', id, error);
					this.emit('error', error, id);
				},
				onplay: (id) => { console.log('Howler.onplay', id); },
				onend: (id) => {
					console.log('Howler.onend', id);
					this.onEnd();
				},
				onpause: (id) => { console.log('Howler.onpause', id); },
				onstop: (id) => { console.log('Howler.onstop', id); },
				onmute: (id) => { console.log('Howler.onmute', id); },
				onvolume: (id) => { console.log('Howler.onvolume', id); },
				onrate: (id) => { console.log('Howler.onrate', id); },
				onseek: (id) => { console.log('Howler.onseek', id); },
				onfade: (id) => { console.log('Howler.onfade', id); },
				onunlock: () => { console.log('Howler.onunlock'); },
			},

		};

		_.merge(this, defaults, config);

		this.formats = [];
	}

	initialize() {
		if (this.debugMode) {
			console.log('HowlerPlayer.initialize');
		}

		if (Howler.noAudio) {
			this.isEnabled = false;
			this.emit('error', 'Howler audio not supported');
			return false;
		}

		Howler.autoUnlock = true;
		Howler.html5PoolSize = 10;
		Howler.autoSuspend = false;
		this.setVolume(this.volume);

		// Howler selects first available format from this list, so ordering is important.
		if (Howler.codecs('webm')) {
			this.formats.push('webm');
		}
		if (Howler.codecs('m4a')) {
			this.formats.push('m4a');
		}
		if (Howler.codecs('aac')) {
			this.formats.push('aac');
		}
		if (Howler.codecs('mp3')) {
			this.formats.push('mp3');
		}

		this.Howler = Howler;

		super.initialize();
	}

	/**
	 * Init song with adapter.
	 * @param {array} files - Array of filenames that represent the same song in different formats (e.g. 'test.mp3', 'test.aac', 'test.mp4')
	 * @param {boolean} isKaraoke - Whether file is karaoke version.
	 * @private
	 */
	async initSong (files, isKaraoke = false) {
		if (this.debugMode) {
			console.log('HowlerPlayer.initSong');
		}
		const sound = await new Promise((resolve, reject) => {
						const sound = new Howl({
							...this.settings,
							xhr: {
								method: 'POST',
								headers: this.headers,
								// withCredentials: true,
							},
							src: _.map(files, (file) => this.baseURL + file),
							onload: () => {
								console.log('Howler.onload');
								resolve(sound);
							},
							onloaderror: (id, error) => {
								console.log('Howler.onloaderror', id, error);
								reject(error);
							},
						});
					})
					.catch((error) => {
						this.emit('error', error);
					});
		if (isKaraoke) {
			this.songSrcKaraoke = sound;
		} else {
			this.songSrc = sound;
		}
	}

	/**
	 * Plays current song in player.
	 */
	playSource = () => {
		if (this.debugMode) {
			console.log('HowlerPlayer.playSource');
		}
		const src = this.getCurrentSrc();
		if (!src) {
			return;
		}
		return src.play();
	}

	/**
	 * Pauses current song in player.
	 */
	pauseSource = () => {
		if (this.debugMode) {
			console.log('HowlerPlayer.pauseSource');
		}
		const src = this.getCurrentSrc();
		if (!src) {
			return;
		}
		return src.pause();
	}

	/**
	 * Scrubs current song to new time.
	 * @param time Number representing destination time in seconds
	 */
	scrub = async (time) => {
		if (this.debugMode) {
			console.log('HowlerPlayer.scrub', time);
		}

		const src = this.getCurrentSrc();
		if (!src) {
			return;
		}
		// if (this.isPlaying) {
		// 	this.pauseSource();
		// }
		// const duration = this.getCurrentDuration();
		// if (time >= (duration - 0.5)) { // Scrubbing to the end fires the onEnd event, and we don't want that.
		// 	time = time - 0.5;
		// }
		await src.seek(time);
		// if (this.isPlaying) {
		// 	await this.playSource();
		// }
	}

	/**
	 * Gets seek time of currentSrc
	 * @returns {number} - # of seconds
	 */
	getCurrentTime = () => {
		const src = this.getCurrentSrc();
		if (!src) {
			return;
		}
		return src.seek();
	}

	/**
	 * Gets duration of currentSrc
	 * @returns {number} - # of seconds
	 */
	getCurrentDuration = () => {
		const src = this.getCurrentSrc();
		if (!src) {
			return;
		}
		return src.duration();
	}

	/**
	 * Set global volume
	 * @param {float} volume - Float number between 0.0 and 1.0.
	 */
	setVolume = (volume) => {
		if (this.debugMode) {
			console.log('HowlerPlayer.setVolume', volume);
		}
		this.volume = volume;
		Howler.volume(volume);
	}

	/**
	 * Directly set playback rate of one file.
	 * @private
	 */
	_setRate = (src, rate) => {
		if (!src) {
			return;
		}
		return src.rate(rate);
	}

	/**
	 * Directly set whether this file loops.
	 * @param {boolean} bool - whether or not to loop
	 * @private
	 */
	 _setIsLooping = (src, bool) => {
		if (!src) {
			return;
		}
		return src.loop(bool);
	}


	destroy() {
		if (this.debugMode) {
			console.log('HowlerPlayer.destroy');
		}
		Howler.unload();

		super.destroy();
	}
}
